import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Container,
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import UserDetailCard from '../components/UserDetailCard';
import LoadingAnimation from '../components/LoadingAnimation';
import { getUserById } from '../services/api';

const DetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getUserById(id);
      setUserData(response.data);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to fetch user details. The user may not exist or there was a server error.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  const handleNewSearchClick = () => {
    navigate('/'); // Go to home page
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #121212 0%, #1E1E1E 100%)',
        py: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated background elements */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'hidden', zIndex: 0, opacity: 0.4 }}>
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              background: `rgba(108, 99, 255, ${Math.random() * 0.1})`,
              borderRadius: '50%',
              width: `${Math.random() * 300 + 100}px`,
              height: `${Math.random() * 300 + 100}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, Math.random() * 100 - 50],
              x: [0, Math.random() * 100 - 50],
              scale: [1, Math.random() + 0.5, 1],
            }}
            transition={{
              duration: Math.random() * 20 + 20,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          />
        ))}
      </Box>
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 2,
              background: 'rgba(30, 30, 30, 0.9)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' }, 
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: { xs: '100%', sm: 'auto' }
              }}>
                <IconButton onClick={handleBackClick} sx={{ mr: 2 }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" component="h1">
                  User Details
                </Typography>
              </Box>
              
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={handleNewSearchClick}
                sx={{ 
                  width: { xs: '100%', sm: 'auto' } 
                }}
              >
                New Search
              </Button>
            </Box>
          </Paper>
        </motion.div>

        {loading ? (
          <LoadingAnimation text="Loading user details..." />
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                background: 'rgba(30, 30, 30, 0.9)',
              }}
            >
              <Typography variant="h6" gutterBottom>
                User not found
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                The user you're looking for might have been removed or doesn't exist.
              </Typography>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleNewSearchClick}
                sx={{ mt: 2 }}
              >
                Return to Search
              </Button>
            </Paper>
          </motion.div>
        ) : (
          <UserDetailCard user={userData} />
        )}
      </Container>
    </Box>
  );
};

export default DetailPage; 